import { FunctionComponent, JSX, useState } from 'react'
import { TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { Daycare } from '@/types/Daycare'
import { useTranslation } from 'react-i18next'
import styles from './requestTableRow.module.scss'
import { Check, Close, ExpandLess, ExpandMore } from '@mui/icons-material'
import moment from 'moment-timezone'
import ConfirmRequestSlot from '../../planning/Dialog/requestSlot/confirmRequestSlot/confirmRequestSlot'
import ConfirmRegularRequest from '../../planning/Dialog/regularRequest/confirmRegularRequest/confirmRegularRequest'
import { OccasionalSlotsPlaces, RegularCancellationRequest, RequestSlot } from '@/types/Slots'
import { ChildParent, ChildRequestSlot, ChildWithParents } from '@/types/Child'
import RequestCell from './requestCell'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'

interface RequestTableRowProps {
    parent: ChildParent
    requestList: (RegularCancellationRequest | RequestSlot)[]
    selectedRequests: (RegularCancellationRequest | RequestSlot)[]
    setSelectedRequests: (value: (RegularCancellationRequest | RequestSlot)[]) => void
    refetchData: () => Promise<void>
    currentDaycare: Daycare
    regularCancellationRequest: RegularCancellationRequest[]
    childs: ChildWithParents[]
    openFamily: boolean
    places: OccasionalSlotsPlaces[]
    canWriteContracts: boolean
    canWriteOccasionalSlot: boolean
}

const RequestTableRow: FunctionComponent<RequestTableRowProps> = ({
    parent,
    requestList,
    selectedRequests,
    setSelectedRequests,
    refetchData,
    currentDaycare,
    regularCancellationRequest,
    childs,
    openFamily,
    places,
    canWriteContracts,
    canWriteOccasionalSlot
}: RequestTableRowProps) => {
    const { t } = useTranslation()

    function isRequestSlot(item: RequestSlot | RegularCancellationRequest): item is RequestSlot {
        return 'daycareId' in item
    }

    function isRegularCancellation(item: RequestSlot | RegularCancellationRequest): item is RegularCancellationRequest {
        return 'reason' in item
    }

    const parentRequests: (RegularCancellationRequest | RequestSlot)[] = requestList.filter(
        (request: RegularCancellationRequest | RequestSlot): boolean => request.parentId === parent.parentId
    )

    const selectableParentRequests: (RegularCancellationRequest | RequestSlot)[] = parentRequests.filter(
        (request: RegularCancellationRequest | RequestSlot): boolean =>
            (isRequestSlot(request) && canWriteOccasionalSlot) || (isRegularCancellation(request) && canWriteContracts)
    )

    const selectedParentRequests: (RegularCancellationRequest | RequestSlot)[] = selectedRequests.filter(
        (selectedRequest: RegularCancellationRequest | RequestSlot): boolean =>
            selectedRequest.parentId === parent.parentId
    )

    const isParentSelected: boolean = selectedParentRequests.length > 0
    const isParentIndeterminate: boolean =
        isParentSelected && selectedParentRequests.length !== selectableParentRequests.length

    const [showRequest, setShowRequest] = useState<boolean>(openFamily)

    function handleParentCheckboxChange(): void {
        if (isParentSelected) {
            setSelectedRequests(
                selectedRequests.filter(
                    (selectedRequest: RegularCancellationRequest | RequestSlot): boolean =>
                        selectedRequest.parentId !== parent.parentId
                )
            )
        } else {
            setSelectedRequests([...selectedRequests, ...selectableParentRequests])
        }
    }

    function isSameRequest(
        a: RegularCancellationRequest | RequestSlot,
        b: RegularCancellationRequest | RequestSlot
    ): boolean {
        return a.id === b.id && isRequestSlot(a) === isRequestSlot(b)
    }

    function handleRequestCheckboxChange(request: RegularCancellationRequest | RequestSlot): void {
        const isRequestSelected: boolean = selectedRequests.some(
            (selectedRequest: RegularCancellationRequest | RequestSlot): boolean =>
                isSameRequest(selectedRequest, request)
        )

        if (isRequestSelected) {
            setSelectedRequests(
                selectedRequests.filter(
                    (selectedRequest: RegularCancellationRequest | RequestSlot): boolean =>
                        !isSameRequest(selectedRequest, request)
                )
            )
        } else {
            setSelectedRequests([...selectedRequests, request])
        }
    }

    const { isRpeOrLaep } = useCurrentDaycareInfos()

    const [acceptRequest, setAcceptRequest] = useState<boolean>(false)

    const [isShowSlotRequest, setShowSlotRequest] = useState<boolean>(false)
    const [childRequestSlots, setChildRequestSlots] = useState<ChildRequestSlot[]>([])

    const [isConfirmRegularRequest, setConfirmRegularRequest] = useState<boolean>(false)
    const [regularRequests, setRegularRequests] = useState<RegularCancellationRequest[]>([])

    async function handleConfirmRequest(
        accept: boolean,
        request: RegularCancellationRequest | RequestSlot
    ): Promise<void> {
        const regularRequest: RegularCancellationRequest | undefined = regularCancellationRequest.find(
            (r: RegularCancellationRequest) => r.id === request.id
        )

        setAcceptRequest(accept)

        if (isRegularCancellation(request) && regularRequest) {
            setConfirmRegularRequest(true)
            setRegularRequests([regularRequest])
        }

        if (isRequestSlot(request)) {
            setShowSlotRequest(true)
            setChildRequestSlots([
                {
                    slotId: request.id,
                    childName: request.childName ? request.childName : '',
                    childComplement: request.childComplement ? request.childComplement : '',
                    childId: request.childId ? request.childId : 0,
                    date: request.date,
                    start: request.start,
                    end: request.end
                }
            ])
        }
    }

    return (
        <>
            <TableHead className={styles.TableHeader}>
                <TableRow>
                    {(canWriteOccasionalSlot || canWriteContracts) && (
                        <TableCell sx={{ width: '15%' }}>
                            <Checkbox
                                checked={isParentSelected && !isParentIndeterminate}
                                indeterminate={isParentIndeterminate}
                                onChange={handleParentCheckboxChange}
                            />
                        </TableCell>
                    )}
                    <TableCell align={'left'} onClick={() => setShowRequest(!showRequest)}>
                        <div className={styles.TableCellIconHeader}>
                            {showRequest ? (
                                <ExpandMore className={styles.IconHeader} />
                            ) : (
                                <ExpandLess className={styles.IconHeader} />
                            )}
                            {parent.lastname && parent.firstname
                                ? parent.lastname + ' ' + parent.firstname
                                : parent.email}
                            {isRpeOrLaep && ` | ${parent.isNanny ? t('assMatShort') : t('parent')}`}
                        </div>
                    </TableCell>
                    {(canWriteOccasionalSlot || canWriteContracts) && <TableCell sx={{ width: '15%' }} />}
                </TableRow>
            </TableHead>
            {showRequest && (
                <TableBody>
                    {[...parentRequests]
                        .sort(
                            (
                                a: RegularCancellationRequest | RequestSlot,
                                b: RegularCancellationRequest | RequestSlot
                            ) => moment(a.date, 'DD-MM-YYYY').diff(moment(b.date, 'DD-MM-YYYY'))
                        )
                        .map(
                            (request: RegularCancellationRequest | RequestSlot): JSX.Element => (
                                <TableRow key={`${isRequestSlot(request) ? 'slot' : 'regular'}-${request.id}`}>
                                    {(canWriteOccasionalSlot || canWriteContracts) && (
                                        <TableCell>
                                            {((isRequestSlot(request) && canWriteOccasionalSlot) ||
                                                (isRegularCancellation(request) && canWriteContracts)) && (
                                                <Checkbox
                                                    checked={selectedRequests.some(
                                                        (
                                                            selectedRequest: RegularCancellationRequest | RequestSlot
                                                        ): boolean => isSameRequest(selectedRequest, request)
                                                    )}
                                                    onChange={() => handleRequestCheckboxChange(request)}
                                                />
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell align={'left'}>
                                        <RequestCell request={request} />
                                    </TableCell>
                                    {(canWriteOccasionalSlot || canWriteContracts) && (
                                        <TableCell align={'center'}>
                                            {((isRequestSlot(request) && canWriteOccasionalSlot) ||
                                                (isRegularCancellation(request) && canWriteContracts)) && (
                                                <div className={styles.TableCellIcon}>
                                                    <Check
                                                        className={styles.Icon}
                                                        onClick={() => handleConfirmRequest(true, request)}
                                                    />
                                                    <Close
                                                        className={styles.Icon}
                                                        onClick={() => handleConfirmRequest(false, request)}
                                                    />
                                                </div>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            )
                        )}
                </TableBody>
            )}
            {isShowSlotRequest && (
                <ConfirmRequestSlot
                    open={isShowSlotRequest}
                    handleClose={() => setShowSlotRequest(false)}
                    accepted={acceptRequest}
                    currentDaycare={currentDaycare}
                    refetchData={refetchData}
                    closeParentDialog={() => setShowSlotRequest(false)}
                    childRequestSlots={childRequestSlots}
                />
            )}
            {isConfirmRegularRequest && (
                <ConfirmRegularRequest
                    open={isConfirmRegularRequest}
                    handleClose={() => setConfirmRegularRequest(false)}
                    regularRequests={regularRequests}
                    childs={childs}
                    accepted={acceptRequest}
                    refetchData={refetchData}
                    closeParentDialog={() => setConfirmRegularRequest(false)}
                    isRpeOrLaep={isRpeOrLaep}
                    places={places}
                />
            )}
        </>
    )
}

export default RequestTableRow
